import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  runInInjectionContext,
  signal,
  type Type,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { ApiService, type ApiServiceKey } from '@core/services/api';
import { TabService } from '@core/services/tab.service';
import { concat, filter, finalize, firstValueFrom, Observable, Subject, take, takeUntil, toArray } from 'rxjs';

import JSZip from 'jszip';

import { extractApiErrorMessage } from '@core/utils/api-error-message';
import { CustomDialogService } from '@core/services/custom-dialog.service';
import { GlobalLoaderService } from '@core/services/global-loader.service';
import { KlConfirmDialogService } from '@core/services/kl-confirm-dialog.service';
import { UserActionAuthService } from '@core/services/user-action-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { KlEntryInfoDialogComponent } from '@shared/components/kl-entry-info-dialog/kl-entry-info-dialog';
import { KlRecordHistoryDialogComponent } from '@shared/components/kl-record-history-dialog/kl-record-history-dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { exportFileBaseName } from '@core/utils/export-file-name';
import { formatKlDateValue } from '../../utils/kl-date-format';
import {
  ActionDto,
  DeleteConfirm,
  DeleteOption,
  EditOption,
  EndpointConfig,
  FilterableItem,
  FilterPayload,
  GridColumn,
  InitState,
  isRowObject,
  KlGridExportScope,
} from './grid.models';
import { KlGridContainer } from './kl-grid-container/kl-grid-container';
import { resolveDblClickAction } from './kl-grid-dblclick.util';
import { evaluateOptionRestrictions, restrictionErrorMessage } from './kl-grid-option-restrictions.util';
import { KlGridRowDetailDirective } from './kl-grid-row-detail.directive';

type ExportCellValue = string | number | boolean | Date | null | undefined | object;

@Component({
  selector: 'kl-grid',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kl-grid.html',
  styleUrl: './kl-grid.scss',
})
export class KlGrid<T extends object = object> implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly customDialog = inject(CustomDialogService);
  private readonly confirmDialog = inject(KlConfirmDialogService);
  private readonly globalLoader = inject(GlobalLoaderService);
  private readonly dynamicDialog = inject(DialogService);
  private readonly injector = inject(Injector);
  private readonly tabService = inject(TabService);
  private readonly actionAuth = inject(UserActionAuthService);

  private readonly destroy$ = new Subject<void>();
  private readonly ownRoute = this.router.url;
  private pendingRefreshRoute: string | null = null;

  readonly container = viewChild.required<KlGridContainer<T>>(KlGridContainer);
  readonly rowDetailTpl = contentChild(KlGridRowDetailDirective);

  // ─── Inputs ───
  readonly endpointConfig = input.required<EndpointConfig>();
  readonly columns = input.required<GridColumn[]>();
  readonly authNum = input.required<string>();
  readonly data = input<T[] | null>(null);
  readonly filterObject = model<Record<string, any>>({});
  readonly sort = input<Array<{ name: string; desc?: boolean }>>([]);
  readonly filterSelectItems = input<FilterableItem[]>([]);
  readonly mode = input<'TAB' | 'PAGE' | 'DIALOG'>('TAB');
  readonly dialogName = input('');
  /** When set, the grid opens add/edit/view via CustomDialogService using this component. */
  readonly formComponent = input<Type<unknown> | null>(null);
  /** Merged into `DynamicDialogConfig.inputValues` when opening the form dialog (e.g. `{ endpoint: 'sector' }`). */
  readonly formDialogExtras = input<Record<string, unknown>>({});
  /** PrimeNG dialog width when `formComponent` opens (wide forms e.g. schedules / number schemes). */
  readonly formDialogWidth = input('min(560px, 94vw)');
  readonly showButton = input(true);
  readonly showToolbar = input(true);
  readonly doAdd = input(true);
  readonly doEdit = input(true);
  readonly doView = input(true);
  readonly doDelete = input(true);
  readonly doBulkDelete = input(true);
  readonly doRefresh = input(true);
  readonly doExport = input(true);
  readonly doOther = input(true);
  readonly refreshOnReturn = input(true);
  readonly dblClickEdit = input(true);
  readonly sortable = input(true);
  readonly filterable = input(false);
  /** Per-column checklist filters on loaded rows (header funnel). */
  readonly columnFilters = input(true);
  /** Header drag-and-drop column reorder (`p-table` / PrimeNG). */
  readonly columnReorderEnabled = input(true);
  /** Resizable column widths on the table header. */
  readonly columnResizeEnabled = input(true);
  readonly showGridSearch = input(false);
  readonly haveHistory = input(false);
  readonly doInfo = input(true);

  /** `.002`/`.003`/`.004`/`.005` role-permission grants layered on top of each feature's own on/off flag. */
  readonly effectiveDoEdit = computed(() => this.doEdit() && this.actionAuth.canShowAction(`${this.authNum()}.002`));
  readonly effectiveDoView = computed(() => this.doView() && this.actionAuth.canShowAction(`${this.authNum()}.003`));
  readonly effectiveDoDelete = computed(
    () => this.doDelete() && this.actionAuth.canShowAction(`${this.authNum()}.004`)
  );
  readonly effectiveDoBulkDelete = computed(
    () => this.doBulkDelete() && this.actionAuth.canShowAction(`${this.authNum()}.004`)
  );
  readonly effectiveHaveHistory = computed(
    () => this.haveHistory() && this.actionAuth.canShowAction(`${this.authNum()}.005`)
  );
  readonly customFilter = input(false);
  readonly fullscreen = input(true);
  readonly doDesign = input(false);
  readonly multi_select = input(false);
  readonly dblSelect = input(false);
  readonly deferResizing = input(false);
  readonly compress = input(false);
  readonly hardRefresh = input(false);
  readonly hasPageLimitBox = input(false);
  /** Opt-in DOM windowing for large row sets — see `KlGridContainer.virtualScroll`. */
  readonly virtualScroll = input(false);
  /** Fixed row height in px for {@link virtualScroll} (must match the CSS row height). */
  readonly virtualScrollItemSize = input(31);
  readonly actions = input<ActionDto[]>([]);
  readonly processesActions = input<ActionDto[]>([]);
  readonly customToolbar = input<ActionDto[]>([]);
  readonly customToolbarVisible = input(true);
  readonly editOptions = input<EditOption[]>([]);
  readonly deleteOptions = input<DeleteOption[]>([]);
  readonly deleteConfirm = input<DeleteConfirm | undefined>(undefined);
  readonly initStatesWithoutChild = input<InitState[]>([]);
  readonly initStatesWithChild = input<InitState[]>([]);
  /** Prime `p-table` `dataKey` for row identity (selection + row expansion). */
  readonly gridDataKey = input<string>('id');
  /** Prime `p-table` `rowExpandMode` when using `klGridRowDetail` row expansion. */
  readonly rowExpandMode = input<'single' | 'multiple'>('multiple');
  readonly historyEndpoint = input('');
  /** Prefix stripped from history section keys for tab labels. */
  readonly historyPrefix = input('');
  readonly autoClose = input(true);
  readonly customDblClick = input<{ method: (payload: T) => void } | undefined>(undefined);
  /** Runs after each load from the API (or zip decode), before selection merge — e.g. join lookups by id. */
  readonly mapRows = input<((rows: T[]) => T[]) | null>(null);
  /** Legacy grid-design parent module (e.g. `MATERIAL_MANAGEMENT`). */
  readonly designParentModule = input('');
  /** Legacy grid-design module (e.g. `MATERIAL_TRANSACTION`). */
  readonly designModule = input('');
  /** Legacy grid-design key (e.g. `MATERIAL_TRANSACTION_TABLE`). */
  readonly designKey = input('');

  // ─── Outputs ───
  readonly dataBinded = output<void>();
  readonly recordDeleted = output<any>();
  readonly onModal = output<any>();
  readonly onCustomFilterEvent = output<any>();
  readonly beforeFilterApplied = output<any>();
  readonly selectionChange = output<T>();
  readonly rowAction = output<{ action: string | undefined; row: T }>();

  // ─── Local state ───
  readonly collectionView = signal<{ sourceCollection: T[] } | null>(null);
  readonly gridRows = signal<any[]>([]);
  readonly pageOffset = signal(0);
  readonly totalCount = signal(0);
  readonly pageSize = signal(10);
  readonly gridDataLoading = signal(false);
  readonly gridLoadingText = signal('');
  readonly recordFetchLoading = signal(false);
  readonly alertShow = signal(false);
  readonly alertType = signal('error');
  readonly alertMessage = signal('');
  readonly disabledButton = signal(false);
  readonly gridData = signal<T[]>([]);

  private _filter: FilterPayload | null = null;

  /**
   * Bound helpers for `kl-grid-container` row actions (`ActionDto.method` context).
   * Matches legacy grids: refresh after lock/unlock + loading overlay during API calls.
   */
  readonly gridActionContext = {
    refresh: () => this.refresh(),
    gridLoading: {
      show: (translationKey: string) => this.showLoading(translationKey),
      hide: () => this.hideLoading(),
    },
    showAlert: (message: string, type: 'error' | 'warn' | 'warning' | 'success' | 'info' = 'error') =>
      this.showAlert(message, type),
    handleError: (error: unknown) => this.handleError(error),
  };

  constructor() {
    afterNextRender(() => {
      runInInjectionContext(this.injector, () => {
        effect(() => {
          const rows = this.data();
          if (rows == null) {
            return;
          }
          this.loadLocalData();
        });
      });
    });
  }

  ngOnInit(): void {
    if (this.data() != null) {
      return;
    }
    // Grids with `designModule` set load a saved fast-filter in kl-grid-container and fetch
    // themselves; skip our own default fetch here so the two don't race.
    if (this.endpointConfig() && !this.designModule().trim()) {
      const defaultFilter: FilterPayload = {
        filter: this.filterObject() ? { ...this.filterObject() } : {},
        query: {
          paging: { limit: this.pageSize(), offset: this.pageOffset(), fetch: 'EAGER' },
        },
        parameters: [],
      };
      this.getDataFromBackend(defaultFilter, false);
    }

    this.tabService.gridRefresh$.pipe(takeUntil(this.destroy$)).subscribe((route) => {
      if (!this.refreshOnReturn() || route !== this.ownRoute) {
        return;
      }
      if (route === this.router.url) {
        this.refresh();
      } else {
        // Save (routed forms) fires this before `location.back()` completes, so the list's
        // URL isn't active yet; re-check once navigation actually lands on it.
        this.pendingRefreshRoute = route;
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.pendingRefreshRoute && this.pendingRefreshRoute === this.router.url) {
          this.pendingRefreshRoute = null;
          this.refresh();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Local data mode ──────────────────────────────────────────────────

  private loadLocalData(): void {
    const data = this.data();
    if (!data) return;
    const mapFn = this.mapRows();
    const full = mapFn ? mapFn([...data]) : data;
    this.totalCount.set(full.length);
    const page = full.slice(this.pageOffset(), this.pageOffset() + this.pageSize());
    this.gridData.set(page);
    this.gridRows.set(page);
    this.collectionView.set({ sourceCollection: page });
    this.disabledButton.set(page.length === 0);
    if (page.length > 0) {
      this.container().currentItem.set(page[0]);
    }
    this.dataBinded.emit();
  }

  // ─── Data loading ─────────────────────────────────────────────────────

  getDataFromBackend(filter: FilterPayload, _onFilter = true): void {
    this.alertShow.set(false);
    this.showLoading('loading');

    // Keep outgoing paging in sync with signals (e.g. p-table paginator changes page size).
    const q = filter.query;
    filter.query = {
      sort: q?.sort,
      paging: {
        limit: this.pageSize(),
        offset: this.pageOffset(),
        fetch: q?.paging?.fetch ?? 'EAGER',
      },
    };

    const filterObj = this.filterObject();
    if (filterObj) {
      Object.assign(filter.filter, filterObj);
    }
    if (this.compress()) {
      filter.filter['compress'] = 'YES';
    }

    const sortVal = this.sort();
    filter.query.sort = sortVal?.length ? sortVal : [{ name: 'id', desc: true }];

    let pendingZip = false;
    const cfg = this.endpointConfig();
    const service = this.microApiToServiceKey(cfg.microApiService);
    this.api
      .getFilteredData<any>(cfg.endpoint, filter, {
        service,
        filterChild: cfg.filterChild,
        hasFilter: cfg.hasFilter,
      })
      .subscribe({
        next: (response: any) => {
          if (!response) {
            this.handleError('error_data');
            return;
          }

          const query = response.query;
          if (query?.paging) {
            const offset = Number(query.paging.offset) || 0;
            const total = query.paging.total ?? 0;
            const limit = Number(query.paging.limit) || this.pageSize();

            // The requested page is beyond the real last page (e.g. its rows were just
            // deleted) — snap back to the actual last page instead of getting stuck on an
            // empty one that "refresh" alone can't recover from. Checked against the paging
            // metadata alone (not response.data.length) so it also covers zip_data responses,
            // where the row count isn't known without decoding first.
            if (offset > 0 && total > 0 && offset >= total) {
              const lastPageOffset = Math.max(0, Math.floor((total - 1) / limit) * limit);
              if (lastPageOffset < offset) {
                this.pageOffset.set(lastPageOffset);
                filter.query.paging.offset = lastPageOffset;
                this.getDataFromBackend(filter);
                return;
              }
            }

            this.pageOffset.set(offset);
            this.totalCount.set(total);
            this.pageSize.set(limit);
          }

          this._filter = filter;

          if (response.zip_data) {
            pendingZip = true;
            this.decodeZipData(response.zip_data).then(
              (data) => {
                this.applyGridData(data);
                this.hideLoading();
              },
              (decodeError) => {
                this.handleError(decodeError ?? 'error_data');
              }
            );
            return;
          }
          const data = response.data ?? [];
          this.applyGridData(data);
        },
        error: (error: any) => this.handleError(error),
        complete: () => {
          if (!pendingZip) this.hideLoading();
        },
      });
  }

  private applyGridData(data: T[]): void {
    let rows = Array.isArray(data) ? data : [];
    const mapFn = this.mapRows();
    if (mapFn) {
      rows = mapFn(rows);
    }
    this.finalizeMappedRows(rows);
  }

  /** Re-runs {@link mapRows} on the current row set without refetching (e.g. after async lookups). */
  reapplyRowMap(): void {
    const mapFn = this.mapRows();
    if (!mapFn) {
      return;
    }
    const rows = mapFn(this.gridRows().map((r) => ({ ...r })));
    this.finalizeMappedRows(rows);
  }

  private finalizeMappedRows(rows: T[]): void {
    let merged = rows;
    if (this.multi_select() && merged.length > 0) {
      const selected = this.container().selectedData() ?? [];
      merged = merged.map((row) => {
        const idx = selected.findIndex((line) => this.entityIdFromRow(line) === this.entityIdFromRow(row));
        return idx !== -1 ? { ...row, selected: true, bulkDeleteSelection: true } : row;
      });
    }
    if (merged.length > 0) {
      this.gridData.set(merged);
      this.gridRows.set(merged);
      this.collectionView.set({ sourceCollection: merged });
      this.disabledButton.set(false);
      this.container().currentItem.set(merged[0]);
    } else {
      this.disabledButton.set(true);
      this.gridData.set([]);
      this.gridRows.set([]);
      this.collectionView.set({ sourceCollection: [] });
    }
    this.dataBinded.emit();
  }

  private decodeZipData(base64: string): Promise<any[]> {
    return JSZip.loadAsync(base64, { base64: true })
      .then((zip) => {
        let file = zip.file('zipFile.json');
        if (!file) {
          const names = Object.keys(zip.files).filter((n) => !zip.files[n].dir && /\.json$/i.test(n));
          if (names.length) {
            file = zip.file(names[0]);
          }
        }
        if (!file) {
          return Promise.reject(new Error('zipFile.json not found'));
        }
        return file.async('string');
      })
      .then((text) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(text);
        } catch {
          return Promise.reject(new Error('Invalid JSON in zip'));
        }
        return this.extractZipPayloadRows(parsed);
      });
  }

  private extractZipPayloadRows(parsed: unknown): any[] {
    if (!parsed || typeof parsed !== 'object') {
      return [];
    }
    const p = parsed as Record<string, unknown>;
    const body = p['body'] as Record<string, unknown> | undefined;
    const raw = body?.['data'] ?? p['data'];

    if (raw == null) {
      return [];
    }
    if (Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'object' && Object.keys(raw as object).length === 0) {
      return [];
    }
    if (typeof raw === 'object') {
      return [raw];
    }
    return [];
  }

  // ─── Filtering ────────────────────────────────────────────────────────

  onFilter(event: any): void {
    const filterInstance = event.filterInstance ?? event;
    // Saved filter_limit can come back as a string ('50') after JSON round-trip.
    const limit = Number(filterInstance?.query?.paging?.limit);
    if (Number.isFinite(limit) && limit > 0 && limit !== this.pageSize()) {
      this.pageSize.set(limit);
      this.pageOffset.set(0);
    }
    this.beforeFilterApplied.emit(filterInstance);
    // Grids fed rows via `[data]` never fetch here — hosts (e.g. kl-transaction client mode)
    // refetch on `beforeFilterApplied`. Saved fast-filter page size still applies locally via
    // the `data()` effect after the host updates `[data]`.
    if (this.data() != null) {
      return;
    }
    this.getDataFromBackend(filterInstance);
  }

  // ─── CRUD ─────────────────────────────────────────────────────────────

  /**
   * Primary key for toolbar CRUD / lock / history: prefers `id`, otherwise the field named by
   * {@link gridDataKey} (supports dotted paths, e.g. flat DTOs with only `current_account_receipt_line_id`).
   */
  private entityIdFromRow(row: object | null | undefined): string | number | undefined {
    if (!isRowObject(row)) return undefined;
    const direct = row['id'];
    if (direct != null && direct !== '') return direct as string | number;
    const key = this.gridDataKey();
    if (!key || key === 'id') return undefined;
    let alt: unknown = row;
    if (!key.includes('.')) {
      alt = row[key];
    } else {
      for (const p of key.split('.')) {
        if (alt == null || typeof alt !== 'object') {
          return undefined;
        }
        alt = (alt as Record<string, unknown>)[p];
      }
    }
    if (alt != null && alt !== '') return alt as string | number;
    return undefined;
  }

  addRecord(initState?: InitState): void {
    if (this.mode() === 'DIALOG') {
      this.openFormDialog('add', initState ?? {});
    } else {
      const extras: NavigationExtras = {
        queryParams: { mode: 'add', type: initState?.payload?.type },
      };
      this.router.navigate([this.router.url + '/form'], extras);
    }
  }

  editRecord(): void {
    if (!this.effectiveDoEdit()) {
      this.handleError('not_authorized_to_perform_this_operation');
      return;
    }
    const currentItem = this.container().currentItem();
    const recordId = this.entityIdFromRow(currentItem);
    if (recordId == null) return;

    const cfg = this.endpointConfig();
    const service = this.microApiToServiceKey(cfg.microApiService);

    const hideGlobalLoader = this.globalLoader.show();
    const finishRecordFetch = (): void => {
      this.recordFetchLoading.set(false);
      hideGlobalLoader();
    };

    this.recordFetchLoading.set(true);
    const editCheckEndpoint = cfg.editConditionCheckEndpoint ?? cfg.endpoint;
    const editCheckChild = cfg.editConditionCheckChildEndpoint;
    this.api.getDataById<any>(editCheckEndpoint, recordId, editCheckChild, service).subscribe({
      next: (entity: any) => {
        if (entity === undefined || entity === null) {
          finishRecordFetch();
          this.handleError('error_data');
          return;
        }

        const restriction = this.evaluateEditRestrictions(entity);
        if (!restriction.allow) {
          const msg = restriction.alertProperty
            ? String(entity[restriction.alertProperty] ?? '') + (restriction.alertMessage ?? '')
            : (restriction.alertMessage ?? 'error_data');
          finishRecordFetch();
          this.handleError(msg);
          return;
        }

        const openEdit = (): void => {
          finishRecordFetch();
          if (this.mode() === 'DIALOG') {
            this.openFormDialog('edit', entity);
          } else {
            this.router.navigate([this.router.url + '/form'], {
              queryParams: { mode: 'edit', id: recordId },
            });
          }
        };

        if (cfg.skipEditLock) {
          openEdit();
          return;
        }

        this.api.lockData(cfg.endpoint, recordId, service).subscribe({
          next: () => openEdit(),
          error: (error: any) => {
            finishRecordFetch();
            this.handleError(error);
          },
        });
      },
      error: (error: any) => {
        finishRecordFetch();
        this.handleError(error);
      },
    });
  }

  private microApiToServiceKey(micro?: string): ApiServiceKey {
    if (micro === 'restaurant' || micro === 'fuel' || micro === 'schedule') {
      return micro;
    }
    return 'api';
  }

  private evaluateEditRestrictions(entity: any): {
    allow: boolean;
    alertProperty?: string;
    alertMessage?: string;
  } {
    return evaluateOptionRestrictions(entity, this.editOptions());
  }

  /** Kayıt Bilgisi — `GET …/{id}/persist`. */
  infoRecord(payload: T): void {
    const infoId = this.entityIdFromRow(payload);
    if (infoId == null) return;
    this.alertShow.set(false);
    const cfg = this.endpointConfig();
    const base = this.historyEndpoint() || cfg.endpoint;
    const service = this.microApiToServiceKey(cfg.microApiService);
    this.runRecordSideFetch(this.api.getChanges<Record<string, unknown>>(base, infoId, service), (resData) => {
      this.dynamicDialog.open(KlEntryInfoDialogComponent, {
        header: this.translate.instant('entry_info'),
        width: 'min(500px, 92vw)',
        modal: true,
        styleClass: 'kl-entry-info-shell',
        closable: true,
        inputValues: { audit: resData },
      });
    });
  }

  /** Kayıt geçmişi — `GET …/{id}/history`. */
  recordHistory(payload: T): void {
    if (!this.effectiveHaveHistory()) {
      this.handleError('not_authorized_to_perform_this_operation');
      return;
    }
    const row = payload != null && this.entityIdFromRow(payload) != null ? payload : this.container().currentItem();
    const histId = this.entityIdFromRow(row);
    if (histId == null) return;

    const titleSuffix = row.code ?? row.description ?? row.document_num;
    this.alertShow.set(false);
    const cfg = this.endpointConfig();
    const base = this.historyEndpoint() || cfg.endpoint;
    const service = this.microApiToServiceKey(cfg.microApiService);
    const path = `${base}/${histId}/history`;

    this.runRecordSideFetch(
      this.api.getAllData<Record<string, unknown>>(path, service),
      (response) => {
        const data = {
          ...response,
          historyPrefix: this.historyPrefix(),
        };
        const header =
          this.translate.instant('record_history') +
          (titleSuffix != null && titleSuffix !== '' ? ' - ' + String(titleSuffix) : '');
        this.dynamicDialog.open(KlRecordHistoryDialogComponent, {
          header,
          width: 'min(960px, 96vw)',
          modal: true,
          styleClass: 'kl-record-history-shell',
          closable: true,
          contentStyle: { overflow: 'auto' },
          inputValues: { entity: data },
        });
      },
      (err) => {
        const status = (err as { status?: number })?.status;
        if (status === 404) {
          this.handleError('record_history_not_supported');
          return;
        }
        this.handleError(err);
      }
    );
  }

  /**
   * Loads audit/history data for a dialog without replacing the grid body with the list loader.
   * Uses {@link recordFetchLoading} (toolbar guard) only — not {@link showLoading}.
   */
  private runRecordSideFetch<T>(
    request$: Observable<T>,
    onSuccess: (data: T) => void,
    onError: (err: unknown) => void = (err) => this.handleError(err)
  ): void {
    this.recordFetchLoading.set(true);
    request$
      .pipe(
        finalize(() => {
          this.recordFetchLoading.set(false);
        })
      )
      .subscribe({
        next: (data) => {
          if (data === undefined || data === null) {
            this.handleError('error_data');
            return;
          }
          onSuccess(data);
        },
        error: onError,
      });
  }

  viewRecord(): void {
    if (!this.effectiveDoView()) {
      this.handleError('not_authorized_to_perform_this_operation');
      return;
    }
    const currentItem = this.container().currentItem();
    const viewId = this.entityIdFromRow(currentItem);
    if (viewId == null) return;

    if (this.mode() === 'DIALOG') {
      this.openFormDialog('view', currentItem, true);
    } else {
      this.router.navigate([this.router.url + '/form'], {
        queryParams: { mode: 'view', id: viewId },
      });
    }
  }

  /**
   * Opens the form component via CustomDialogService when `formComponent` is set;
   * otherwise falls back to emitting `onModal`.
   */
  private openFormDialog(mode: 'add' | 'edit' | 'view', entity: unknown, isReadOnly = false): void {
    const FormCmp = this.formComponent();
    if (FormCmp) {
      const modalId = `grid-${FormCmp.name || this.endpointConfig().endpoint}-dialog`;
      const entityPayloadType =
        entity && typeof entity === 'object' && 'payload' in (entity as Record<string, unknown>)
          ? ((entity as Record<string, unknown>)['payload'] as Record<string, unknown>)?.['type']
          : undefined;
      const header =
        mode === 'add'
          ? `${this.translate.instant('new')} ${this.translate.instant(
              (entityPayloadType as string) ?? this.dialogName()
            )}`
          : this.translate.instant(this.dialogName());

      const ref = this.customDialog.open({
        modalId,
        component: FormCmp,
        header,
        width: this.formDialogWidth(),
        inputValues: {
          mode,
          entity: entity ?? {},
          isReadOnly,
          ...this.formDialogExtras(),
          showFormTitle: false,
        },
      });
      ref?.onClose.pipe(take(1)).subscribe((result) => {
        if (result && typeof result === 'object' && 'saved' in result && (result as { saved?: boolean }).saved) {
          this.refresh();
        }
      });
    } else {
      this.onModal.emit({ mode, entity, title: this.translate.instant(this.dialogName()), isReadOnly });
    }
  }

  deleteRecord(): void {
    if (!this.effectiveDoDelete()) {
      this.handleError('not_authorized_to_perform_this_operation');
      return;
    }
    const currentItem = this.container().currentItem();
    const id = this.entityIdFromRow(currentItem);
    if (id == null) return;

    const cfg = this.endpointConfig();
    const service = this.microApiToServiceKey(cfg.microApiService);

    this.showLoading('loading');
    this.api.getDataById<any>(cfg.specialGetByIdEndpoint ?? cfg.endpoint, id, undefined, service).subscribe({
      next: (entity: any) => {
        this.hideLoading();
        if (entity === undefined || entity === null) {
          this.handleError('error_data');
          return;
        }

        const restriction = evaluateOptionRestrictions(entity, this.deleteOptions());
        if (!restriction.allow) {
          this.handleError(restrictionErrorMessage(entity, restriction));
          return;
        }

        this.confirmDeleteAndRun(entity, id);
      },
      error: (error: any) => {
        this.hideLoading();
        this.handleError(error);
      },
    });
  }

  bulkDeleteRecords(selectedRecords: T[]): void {
    if (!this.effectiveDoBulkDelete()) {
      this.handleError('not_authorized_to_perform_this_operation');
      return;
    }
    if (!selectedRecords.length) return;
    const rows = [...selectedRecords];

    for (const record of rows) {
      const restriction = evaluateOptionRestrictions(record, this.deleteOptions());
      if (!restriction.allow) {
        this.handleError(restrictionErrorMessage(record, restriction));
        return;
      }
    }

    this.confirmDialog
      .open({
        title: 'delete_selected',
        description: 'confirm_delete_records_bulk_message',
        icon: 'ph ph-trash',
      })
      .pipe(take(1))
      .subscribe((ok: boolean) => {
        if (ok) this.runBulkDelete(rows);
      });
  }

  /** Plain confirm, or (when `deleteConfirm()` applies) a checkbox offering to also delete a related record. */
  private confirmDeleteAndRun(entity: any, id: string | number): void {
    const dc = this.deleteConfirm();
    const plainConfirm = () =>
      this.confirmDialog
        .open({ title: 'delete', description: 'confirm_delete_record_message', icon: 'ph ph-trash' })
        .pipe(take(1))
        .subscribe((ok) => {
          if (ok) this.runSingleDelete(id);
        });

    if (!dc) {
      plainConfirm();
      return;
    }
    if (dc.skipConfirmation) {
      this.confirmDialog
        .open({ title: 'delete', description: 'confirm_delete_record_message', icon: 'ph ph-trash' })
        .pipe(take(1))
        .subscribe((ok) => {
          if (ok) this.runSingleDelete(id, dc.delete_related);
        });
      return;
    }

    const deleteWithConfirm = dc.condition
      ? !dc.condition.values?.includes(String(entity[dc.condition.control ?? '']))
      : !!dc.deleteWithConfirm;

    if (deleteWithConfirm && dc.inputPlaceholder) {
      this.confirmDialog
        .openWithCheckbox({
          title: 'delete',
          description: 'confirm_delete_record_message',
          icon: 'ph ph-trash',
          checkboxLabel: dc.inputPlaceholder,
        })
        .pipe(take(1))
        .subscribe(({ confirmed, checked }) => {
          if (confirmed) this.runSingleDelete(id, checked ? dc.delete_related : undefined);
        });
      return;
    }
    plainConfirm();
  }

  private runSingleDelete(id: string | number, targetEndpoint?: string): void {
    this.showLoading('deleting');
    const cfg = this.endpointConfig();
    const service = this.microApiToServiceKey(cfg.microApiService);
    this.api.deleteData(targetEndpoint ?? cfg.endpoint, id, service).subscribe({
      next: () => {
        this.recordDeleted.emit(id);
        if (this._filter) this.getDataFromBackend(this._filter);
      },
      error: (error: any) => {
        this.hideLoading();
        this.handleError(error);
      },
      complete: () => this.hideLoading(),
    });
  }

  private runBulkDelete(selectedRecords: any[]): void {
    const cfg = this.endpointConfig();
    const service = this.microApiToServiceKey(cfg.microApiService);
    const requests = selectedRecords
      .map((r) => this.entityIdFromRow(r))
      .filter((id): id is string | number => id != null)
      .map((id) => this.api.deleteData(cfg.endpoint, id, service));
    if (!requests.length) {
      return;
    }

    this.showLoading('loading');

    concat(...requests)
      .pipe(
        finalize(() => {
          this.hideLoading();
          this.container().cancelBulkDelete();
        }),
        toArray()
      )
      .subscribe({
        next: () => {
          if (this._filter) this.getDataFromBackend(this._filter);
        },
        error: (error: any) => this.handleError(error),
      });
  }

  dblClickCall(): void {
    const action = resolveDblClickAction({
      dblSelect: this.dblSelect(),
      hasCustomDblClick: !!this.customDblClick(),
      dblClickEdit: this.dblClickEdit(),
      doEdit: this.effectiveDoEdit(),
    });
    if (action === 'custom') {
      this.customDblClick()!.method(this.container().currentItem());
    } else if (action === 'edit') {
      this.editRecord();
    }
  }

  // ─── Pagination ───────────────────────────────────────────────────────

  movePage(offset: number): void {
    this.pageOffset.set(offset);
    if (this.data()) {
      this.loadLocalData();
      return;
    }
    if (!this._filter) return;
    this._filter.query.paging.offset = offset;
    this.getDataFromBackend(this._filter);
  }

  /** p-table paginator (server-side lazy paging). */
  onTablePaginatorChange(event: { first: number; rows: number }): void {
    const rows = Math.max(1, Math.min(999, event.rows));
    if (rows !== this.pageSize()) {
      this.pageSize.set(rows);
    }
    this.movePage(event.first);
  }

  // ─── Refresh ──────────────────────────────────────────────────────────

  refresh(): void {
    if (this.data()) {
      this.loadLocalData();
      return;
    }
    if (this.hardRefresh()) {
      this.filterObject.set({});
      const cleanFilter: FilterPayload = {
        filter: {},
        query: {
          paging: {
            limit: this._filter?.query.paging.limit ?? 10,
            offset: 0,
            fetch: 'EAGER',
          },
        },
        parameters: [],
      };
      this.getDataFromBackend(cleanFilter);
      return;
    }
    if (this._filter) {
      this._filter.query.paging.offset = this.pageOffset();
      this.getDataFromBackend(this._filter);
    }
  }

  // ─── Export ───────────────────────────────────────────────────────────

  private static readonly EXPORT_ROW_LIMIT = 999999;

  onExport(scope: KlGridExportScope): void {
    const localData = this.data();
    if (localData != null) {
      const mapFn = this.mapRows();
      const full = mapFn ? mapFn([...localData]) : [...localData];
      if (scope === 'page') {
        const page = full.slice(this.pageOffset(), this.pageOffset() + this.pageSize());
        this.exportToXlsx(page);
      } else {
        this.exportToXlsx(full);
      }
      return;
    }

    if (scope === 'page') {
      this.exportToXlsx(this.container().filteredData());
      return;
    }

    this.showLoading('loading');
    this.resolveExportRows(scope)
      .then((rows) => {
        this.hideLoading();
        this.exportToXlsx(rows);
      })
      .catch(() => {
        this.hideLoading();
        this.handleError('export_failed');
      });
  }

  private resolveExportRows(scope: 'filtered' | 'all'): Promise<any[]> {
    const exportPaging = { offset: 0, limit: KlGrid.EXPORT_ROW_LIMIT, fetch: 'EAGER' };

    if (scope === 'filtered') {
      if (!this._filter) {
        return Promise.reject(new Error('export_failed'));
      }
      const filterPayload: FilterPayload = {
        filter: { ...this._filter.filter },
        query: {
          sort: this._filter.query.sort,
          paging: exportPaging,
        },
        parameters: this._filter.parameters ? [...this._filter.parameters] : [],
      };
      return this.fetchExportRows(filterPayload);
    }

    const filterPayload: FilterPayload = {
      filter: { ...this.filterObject() },
      query: { paging: exportPaging },
      parameters: [],
    };
    return this.fetchExportRows(filterPayload);
  }

  private finalizeExportFilter(filter: FilterPayload): FilterPayload {
    const prepared: FilterPayload = {
      filter: { ...filter.filter },
      query: {
        sort: filter.query?.sort,
        paging: {
          limit: filter.query.paging.limit,
          offset: filter.query.paging.offset ?? 0,
          fetch: filter.query.paging.fetch ?? 'EAGER',
        },
      },
      parameters: filter.parameters ? [...filter.parameters] : [],
    };
    if (this.compress()) {
      prepared.filter['compress'] = 'YES';
    }
    const sortVal = this.sort();
    prepared.query.sort = sortVal?.length ? sortVal : [{ name: 'id', desc: true }];
    return prepared;
  }

  private mapRowsForExport(data: any[]): any[] {
    const rows = Array.isArray(data) ? data : [];
    const mapFn = this.mapRows();
    return mapFn ? mapFn(rows) : rows;
  }

  private fetchExportRows(filter: FilterPayload): Promise<any[]> {
    return this.requestExportResponse(filter).then(({ rows }) => rows);
  }

  private requestExportResponse(
    filter: FilterPayload
  ): Promise<{ rows: any[]; total: number }> {
    const cfg = this.endpointConfig();
    const service = this.microApiToServiceKey(cfg.microApiService);
    const payload = this.finalizeExportFilter(filter);
    return firstValueFrom(
      this.api.getFilteredData<object>(cfg.endpoint, payload, {
        service,
        filterChild: cfg.filterChild,
        hasFilter: cfg.hasFilter,
      })
    ).then((response) => {
      if (!response || typeof response !== 'object') {
        return Promise.reject(new Error('export_failed'));
      }
      const body = response as {
        zip_data?: string;
        data?: any[];
        query?: { paging?: { total?: number } };
      };
      const total = body.query?.paging?.total ?? body.data?.length ?? 0;
      if (body.zip_data) {
        return this.decodeZipData(body.zip_data).then((data) => ({
          rows: this.mapRowsForExport(data),
          total,
        }));
      }
      return {
        rows: this.mapRowsForExport(body.data ?? []),
        total,
      };
    });
  }

  /** Supports `binding` paths like `current_account.code` for nested row objects. */
  private resolveExportCellValue(
    row: Record<string, ExportCellValue> | null | undefined,
    binding: string
  ): ExportCellValue {
    if (row == null || binding === '') return undefined;
    if (!binding.includes('.')) return row[binding];
    return binding.split('.').reduce<ExportCellValue>((acc, key) => {
      if (acc == null || typeof acc !== 'object' || acc instanceof Date) return undefined;
      return (acc as Record<string, ExportCellValue>)[key];
    }, row);
  }

  private exportToXlsx(rows: any[]): void {
    // Checkbox selection columns are UI, not data.
    const cols = this.container()
      .visibleColumns()
      .filter((c) => !c.multi_select);
    const cfg = this.endpointConfig();
    const name = exportFileBaseName(this.translate, this.tabService, cfg.name ?? 'export');

    const headers = cols.map((c) => (c.header ? this.translate.instant(c.header) : (c.binding ?? '')));

    const dataRows = rows.map((row) =>
      cols.map((col) => {
        const raw = this.resolveExportCellValue(row, col.binding!);
        // translate.instant throws on an empty key, which would abort the export
        if (raw == null || raw === '') return '';
        if (col.format === 'date') return formatKlDateValue(raw, false);
        if (col.format === 'date-time') return formatKlDateValue(raw, true);
        if (col.translate) return this.translate.instant(String(raw));
        return raw;
      })
    );

    Promise.all([import('xlsx-js-style'), import('../../utils/kl-xlsx-sheet')])
      .then(([mod, { formatXlsxSheet }]) => {
        // CJS package; production build can nest exports under `default`.
        const XLSX = mod.default ?? mod;
        const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
        formatXlsxSheet(
          ws,
          cols.map((col) => col.displayFractionDigits)
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, name);
        XLSX.writeFile(wb, `${new Date().toISOString().slice(0, 10)}-${name}.xlsx`);
      })
      .catch((err) => {
        console.error(err);
        this.handleError('export_failed');
      });
  }

  // ─── Utilities ────────────────────────────────────────────────────────

  showLoading(text: string): void {
    this.gridDataLoading.set(true);
    this.gridLoadingText.set(text);
  }

  hideLoading(): void {
    this.gridDataLoading.set(false);
    this.gridLoadingText.set('');
  }

  showAlert(message: string, type: 'error' | 'warn' | 'warning' | 'success' | 'info' = 'error'): void {
    this.alertShow.set(true);
    this.alertType.set(type === 'warn' ? 'warning' : type);
    this.alertMessage.set(message);
    this.recordFetchLoading.set(false);
    this.hideLoading();
  }

  handleError(error: unknown): void {
    this.showAlert(extractApiErrorMessage(error as object), 'error');
  }

  closeAlert(): void {
    this.alertShow.set(false);
    this.alertMessage.set('');
  }
}
